/* eslint-disable sonarjs/cognitive-complexity */
import React, { FC, useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { CheckIcon } from '@/atoms/Icons/CheckIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { Image } from '@/atoms/Image'
import { CaptionSM, EyebrowLG, EyebrowMD, EyebrowSM, HeadingLG, HeadingXS, TitleMD } from '@/atoms/Text'
import { slugs } from '@/constants'
import { Modal } from '@/molecules/Modal'
import { useTranslate } from '@/utils/translate/translate-client'
import { TheatricalPromotion } from '@/views/TicketCheckoutViews/ShowtimesView/components/TheatricalPromotionContext'
import { FlexibleBookingToolTip } from '@/views/TicketCheckoutViews/ShowtimesView/components/ToolTips/FlexibleBookingToolTip'
import { SpecificTimesToolTip } from '@/views/TicketCheckoutViews/ShowtimesView/components/ToolTips/SpecificTimesToolTip'
import TimeLeft from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/DiscountBanner/TimeLeft'

interface Props {
  onClose: () => void
  open: boolean
  theatricalSlug: string
  utcTimeUntilDiscountExpires?: string | null
  discount: TheatricalPromotion['discount']
  code: TheatricalPromotion['code']
}

const EarlyBirdModal: FC<Props> = ({ open, onClose, utcTimeUntilDiscountExpires, discount, theatricalSlug, code }) => {
  const { t } = useTranslate('tickets')

  const [copiedCode, setCopiedCode] = useState(false)

  const handleCopyCode = useCallback(() => {
    if (code != null) {
      navigator.clipboard.writeText(code)
      setCopiedCode(true)
    }
  }, [code])

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>

    if (copiedCode) {
      timer = setTimeout(() => {
        setCopiedCode(false)
      }, 3000)
    }

    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [copiedCode, setCopiedCode])

  if (theatricalSlug === slugs.kingOfKings) {
    return (
      <Modal
        backgroundScreenColor="black"
        isOpen={open}
        onClose={onClose}
        panelClasses="!bg-[url(https://images.angelstudios.com/image/upload/v1741372292/tkok-promo-modal-bg-img-01_nn7vug.jpg)] bg-bottom bg-cover !text-white w-full !max-w-[380px] shadow-dark-4 rounded-3xl !p-6 !pt-10 relative text-center !overflow-visible"
        transition="appear"
        className="z-[99999]"
      >
        <div>
          <div className="absolute right-6 top-6 cursor-pointer rounded-full bg-white/20 p-1" onClick={onClose}>
            <CloseIcon color="white" size={18} />
          </div>
          <FlexColumn className="relative z-10 gap-6">
            <FlexColumn className="relative mx-auto w-full max-w-[177px] rounded-2xl border border-white/30 px-3 py-2">
              <EyebrowSM className="absolute -top-2 left-1/2 -translate-x-1/2 px-1 uppercase">
                {t('endingInCount', 'Ending In')}
              </EyebrowSM>
              <TimeLeft isModal className="gap-4 border-white/30" />
            </FlexColumn>
            <FlexColumn className="relative z-20 shrink-0 items-center gap-1.5">
              <HeadingLG className="uppercase" weight="bold">
                {discount}% Off
              </HeadingLG>
              <FlexColumn className="items-center gap-0.5">
                <FlexRow className="gap-1">
                  <EyebrowMD className="text-[0.938rem] uppercase">{t('useCode', 'Use Code')}</EyebrowMD>
                  <div
                    className={classNames('relative flex h-[26px] items-center justify-center overflow-hidden px-1')}
                  >
                    <EyebrowMD weight="bold" className="relative z-10 bg-[#EF3E4E] p-1 text-[0.938rem] uppercase">
                      {code}
                    </EyebrowMD>
                  </div>
                </FlexRow>
                <EyebrowMD className="text-[0.938rem] uppercase">{t('atCheckout', 'At Checkout')}</EyebrowMD>
              </FlexColumn>
            </FlexColumn>

            <FlexColumn className="gap-1">
              <TitleMD weight="bold">{t('OnSelectShowtimes', 'On Select Showtimes')}</TitleMD>
              <EyebrowMD className="opacity-70">OPENING WEEK (Apr 10-17)</EyebrowMD>
              <FlexRow className="justify-center gap-1">
                <SpecificTimesToolTip />
              </FlexRow>
              <FlexibleBookingToolTip
                className="mx-auto w-fit pt-5"
                panelPosition="CENTER"
                buttonClassName={classNames(
                  'backdrop-blur border-none w-full',
                  theatricalSlug === slugs.soundOfHopeTheStoryOfPossumTrot ? 'bg-white/20' : 'bg-black/30',
                )}
              />
            </FlexColumn>
            <FlexColumn className="items-center gap-3">
              <Button onClick={handleCopyCode} className="flex w-fit items-center gap-1" variant="white">
                {copiedCode ? (
                  <CheckIcon size={24} color="core-gray-950" />
                ) : (
                  <CopyIcon size={24} color="core-gray-950" />
                )}
                {copiedCode ? t('copiedCode', 'Copied Code') : t('copyCode', 'Copy Code')}
              </Button>
              <CaptionSM italic color="core-gray-400">
                {t('availableForAngelPartneringTheaters', 'Available for Angel Partnering Theaters.')}
              </CaptionSM>
            </FlexColumn>
          </FlexColumn>
        </div>
      </Modal>
    )
  }
  if (theatricalSlug === slugs.theLastRodeo) {
    return (
      <Modal
        backgroundScreenColor="black"
        isOpen={open}
        onClose={onClose}
        panelClasses="!bg-[#EAE0D9] !bg-[url(https://images.angelstudios.com/image/upload/v1743821891/dirt-texture-on-white-01_vowmnm.jpg)] bg-bottom bg-cover !text-white w-full !max-w-[380px] shadow-dark-4 rounded-3xl !p-6 !pt-10 relative text-center !overflow-visible before:content-[''] before:absolute before:inset-0 before:bg-[#EAE0D9] before:opacity-80 before:rounded-3xl"
        transition="appear"
        className="z-[99999]"
      >
        <div className="]">
          <div className="absolute right-6 top-6 cursor-pointer rounded-full bg-black/10 p-1" onClick={onClose}>
            <CloseIcon color="black" size={18} />
          </div>
          <FlexColumn className="relative z-10 gap-6 text-black">
            <FlexColumn className="relative z-20 shrink-0 items-center gap-1.5">
              <HeadingXS weight="bold">{t('modalEarlyBirdTitle', 'Buy Early. Save Big.')}</HeadingXS>
              <HeadingLG className="uppercase" weight="bold">
                {discount}% Off
              </HeadingLG>
              <FlexColumn className="items-center gap-0.5">
                <FlexRow className="gap-1 bg-[#B22E14] text-white">
                  <EyebrowMD className="pl-2 text-[0.938rem] uppercase">{t('useCode', 'Use Code')}</EyebrowMD>
                  <div
                    className={classNames('relative flex h-[26px] items-center justify-center overflow-hidden px-1')}
                  >
                    <EyebrowMD weight="bold" className="relative z-10 bg-[##B22E14] p-1 text-[0.938rem] uppercase">
                      {code}
                    </EyebrowMD>
                  </div>
                </FlexRow>
                <EyebrowMD className="text-[0.938rem] uppercase"></EyebrowMD>
              </FlexColumn>
            </FlexColumn>

            <FlexColumn className="relative z-30 gap-1">
              <TitleMD weight="bold">{t('OnSelectShowtimes', 'On Select Showtimes')}</TitleMD>
              <EyebrowLG className="uppercase opacity-70">OPENING WEEK (May 22–25)</EyebrowLG>
              <FlexRow className="mb-3 justify-center gap-1">
                <SpecificTimesToolTip />
              </FlexRow>
              <FlexColumn className="relative mx-auto w-full max-w-[177px] rounded-2xl border border-white/30 px-3 pb-0 pt-2">
                <EyebrowSM className="absolute -top-2 left-1/2 -translate-x-1/2 px-1 uppercase">
                  {t('endingInCount', 'Ending In')}
                </EyebrowSM>
                <TimeLeft isModal className="gap-4 border-white/30" />
              </FlexColumn>
              <FlexibleBookingToolTip
                className="mx-auto w-fit pt-2"
                panelPosition="CENTER"
                buttonColor={'black'}
                buttonClassName={classNames(
                  'backdrop-blur border-[1px] !border-black w-full  opacity-60',
                  theatricalSlug === slugs.soundOfHopeTheStoryOfPossumTrot ? 'bg-white/20' : 'bg-none ',
                )}
              />
            </FlexColumn>
            <FlexColumn className="items-center gap-3">
              <Button onClick={handleCopyCode} className="flex w-fit items-center gap-1" variant="black">
                {copiedCode ? <CheckIcon size={24} color="white" /> : <CopyIcon size={24} color="white" />}
                {copiedCode ? t('copiedCode', 'Copied Code') : t('copyCode', 'Copy Code')}
              </Button>
              <CaptionSM italic color="core-gray-600">
                {t('availableForAngelPartneringTheaters', 'Available for Angel Partnering Theaters.')}
              </CaptionSM>
            </FlexColumn>
          </FlexColumn>
        </div>
      </Modal>
    )
  }
  return (
    <Modal
      backgroundScreenColor="black"
      isOpen={open}
      onClose={onClose}
      panelClasses="!bg-[url(https://images.angelstudios.com/image/upload/v1727392065/bonhoeffer/bonhoeffer_early_bird_modal.jpg)] bg-bottom bg-cover !text-white !max-w-[380px] shadow-dark-4 rounded-3xl !p-6 !pt-10 relative text-center !overflow-visible"
      transition="appear"
      className="z-[99999]"
    >
      <div>
        <div className="absolute right-6 top-6 cursor-pointer rounded-full bg-white/20 p-1" onClick={onClose}>
          <CloseIcon color="white" size={18} />
        </div>
        <FlexColumn className="relative z-10 gap-6">
          {utcTimeUntilDiscountExpires && (
            <FlexColumn className="relative mx-auto w-full max-w-[177px] rounded-2xl border border-white/30 px-3 py-2">
              <EyebrowSM className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#6b1e22] px-1 uppercase">
                {t('endingInCount', 'Ending In')}
              </EyebrowSM>
              <TimeLeft isModal className="gap-4 border-white/30" />
            </FlexColumn>
          )}
          <FlexColumn className="relative z-20 shrink-0 items-center gap-1.5">
            {/*<HeadingLG className="uppercase" weight="bold">*/}
            {/*  {adjustedDiscount ? `${adjustedDiscount}%` : discount?.description} {t('offEarlyBird', 'off')}*/}
            {/*</HeadingLG>*/}
            <FlexColumn className="items-center gap-0.5">
              <FlexRow className="gap-1">
                <EyebrowMD className="text-[0.938rem] uppercase">{t('useCode', 'Use Code')}</EyebrowMD>
                <div className={classNames('relative flex h-[26px] items-center justify-center overflow-hidden px-1')}>
                  <EyebrowMD
                    weight="bold"
                    color={theatricalSlug === slugs.soundOfHopeTheStoryOfPossumTrot ? 'white' : 'black'}
                    className="relative z-10 text-[0.938rem] uppercase"
                  >
                    {code}
                  </EyebrowMD>
                  {theatricalSlug !== slugs.soundOfHopeTheStoryOfPossumTrot && (
                    <Image
                      className="absolute inset-0 scale-150 p-1"
                      alt=""
                      src={`/v1712075285/brush-stroke-orange-01.svg`}
                      fill
                    />
                  )}
                </div>
              </FlexRow>
              <EyebrowMD className="text-[0.938rem] uppercase">{t('atCheckout', 'At Checkout')}</EyebrowMD>
            </FlexColumn>
          </FlexColumn>

          <FlexColumn className="gap-1">
            <TitleMD weight="bold">{t('OnSelectShowtimes', 'On Select Showtimes')}</TitleMD>
            {theatricalSlug === slugs.bonhoeffer && (
              <EyebrowMD className="opacity-70">OPENING WEEK (Nov 21-24)</EyebrowMD>
            )}
            <FlexRow className="justify-center gap-1">
              <SpecificTimesToolTip />
            </FlexRow>
            <FlexibleBookingToolTip
              className="mx-auto w-fit pt-5"
              panelPosition="CENTER"
              buttonClassName={classNames(
                'backdrop-blur border-none w-full',
                theatricalSlug === slugs.soundOfHopeTheStoryOfPossumTrot ? 'bg-white/20' : 'bg-black/30',
              )}
            />
          </FlexColumn>
          <FlexColumn className="items-center gap-3">
            <Button onClick={handleCopyCode} className="flex w-fit items-center gap-1" variant="white">
              {copiedCode ? (
                <CheckIcon size={24} color="core-gray-950" />
              ) : (
                <CopyIcon size={24} color="core-gray-950" />
              )}
              {copiedCode ? t('copiedCode', 'Copied Code') : t('copyCode', 'Copy Code')}
            </Button>
            <CaptionSM italic color="core-gray-400">
              {t('availableForAngelPartneringTheaters', 'Available for Angel Partnering Theaters.')}
            </CaptionSM>
          </FlexColumn>
        </FlexColumn>
      </div>
    </Modal>
  )
}

export default EarlyBirdModal
